import { createContext, useContext, useMemo, useState, type ReactNode } from 'react';

type FiltersContextValue = {
  activeCity: string;
  setActiveCity: (city: string) => void;
  activeDate: string;
  setActiveDate: (date: string) => void;
  customDate: Date | null;
  setCustomDate: (date: Date | null) => void;
  activeCategory: string;
  setActiveCategory: (category: string) => void;
  reset: () => void;
};

const DEFAULT_CITY = 'all';
const DEFAULT_DATE = 'alltime';
const DEFAULT_CATEGORY = 'all';

const FiltersContext = createContext<FiltersContextValue | null>(null);

export function FiltersProvider({ children }: { children: ReactNode }) {
  const [activeCity, setActiveCity] = useState<string>(DEFAULT_CITY);
  const [activeDate, setActiveDate] = useState<string>(DEFAULT_DATE);
  const [customDate, setCustomDate] = useState<Date | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>(DEFAULT_CATEGORY);

  const value = useMemo<FiltersContextValue>(
    () => ({
      activeCity,
      setActiveCity,
      activeDate,
      setActiveDate,
      customDate,
      setCustomDate,
      activeCategory,
      setActiveCategory,
      reset: () => {
        setActiveCity(DEFAULT_CITY);
        setActiveDate(DEFAULT_DATE);
        setCustomDate(null);
        setActiveCategory(DEFAULT_CATEGORY);
      },
    }),
    [activeCity, activeDate, customDate, activeCategory],
  );

  return <FiltersContext.Provider value={value}>{children}</FiltersContext.Provider>;
}

export function useFilters() {
  const ctx = useContext(FiltersContext);
  if (!ctx) {
    throw new Error('useFilters must be used within a FiltersProvider');
  }
  return ctx;
}
